import { useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { IconButton, TextInput } from "@narmi/design_system";

import styles from "./SavedFiltersPanel.module.scss";

import type { TransactionSavedFilter } from "../../hooks/useTransactionSavedFilters";

interface SavedFiltersPanelProps {
  savedFilters: TransactionSavedFilter[];
  isLoading: boolean;
  onSelectSavedFilter: (filterId: string) => void;
  onDeleteSavedFilter: (filterId: string) => void;
  close: () => void;
}

const SavedFiltersPanel = ({
  savedFilters,
  isLoading,
  onSelectSavedFilter,
  onDeleteSavedFilter,
  close,
}: SavedFiltersPanelProps) => {
  const { l10n } = useLocalization();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return savedFilters;
    const query = searchQuery.trim().toLowerCase();
    return savedFilters.filter((savedFilter) =>
      savedFilter.name.toLowerCase().includes(query)
    );
  }, [savedFilters, searchQuery]);

  const handleSelectSavedFilter = (filterId: string) => {
    onSelectSavedFilter(filterId);
    close();
  };

  const hasNoSavedFilters = !isLoading && savedFilters.length === 0;
  const hasNoSearchResults =
    !isLoading && savedFilters.length > 0 && filteredResults.length === 0;

  return (
    <div className={styles["saved-filters-panel"]}>
      <div className={styles["saved-filters-panel-search"]}>
        <TextInput
          label={l10n.getString("saved-filters-search")}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          testId="saved-filters-search"
        />
      </div>
      <div className={styles["saved-filters-panel-divider"]} />
      <div className={styles["saved-filters-panel-list"]}>
        {isLoading && (
          <div
            className={styles["saved-filters-panel-empty"]}
            data-testid="saved-filters-loading"
          >
            {l10n.getString("saved-filters-loading")}
          </div>
        )}
        {hasNoSavedFilters && (
          <div
            className={styles["saved-filters-panel-empty"]}
            data-testid="saved-filters-empty"
          >
            {l10n.getString("saved-filters-empty")}
          </div>
        )}
        {hasNoSearchResults && (
          <div
            className={styles["saved-filters-panel-empty"]}
            data-testid="saved-filters-no-results"
          >
            {l10n.getString("saved-filters-no-results")}
          </div>
        )}
        {!isLoading &&
          filteredResults.map((savedFilter) => (
            <div
              key={savedFilter.id}
              className={styles["saved-filters-panel-item"]}
              data-testid={`saved-filter-${savedFilter.id}`}
              onClick={() => handleSelectSavedFilter(savedFilter.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectSavedFilter(savedFilter.id);
                }
              }}
            >
              <span className={styles["saved-filters-panel-item-label"]}>
                {savedFilter.name}
              </span>
              <span
                className={styles["saved-filters-panel-item-delete"]}
                data-testid={`delete-saved-filter-${savedFilter.id}`}
                onKeyDown={(e: React.KeyboardEvent) => {
                  e.stopPropagation();
                }}
              >
                <IconButton
                  kind="plain"
                  name="trash"
                  label={l10n.getString("saved-filters-delete-label")}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onDeleteSavedFilter(savedFilter.id);
                  }}
                />
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SavedFiltersPanel;
